<template>
  <div class="content dark federated-results">
    <header class="container">
      <div class="breadcrumb">
        <a class="dark" :href="breadcrumb.url">{{ breadcrumb.title }}</a>
      </div>
      <h1>Search Our Collections</h1>
    </header>

    <div ref="results" class="grid-content container">
      <article class="content__main">
        <h2 class="content__subhead">
          <p>
            {{ headerCopy }}
          </p>
        </h2>
      </article>
    </div>

    <div class="content__full-width container">
      <ahc-federated-search-searchbar
        action="/learning-research/search-the-collections/federated-search-results"
        :submit-handler="submitHandler"
        :default-value="searchParam"
        :help-text="helpText"
        :placeholder="`search by keyword, topic, neighborhood...`"
        inputLabel="Federated Search"
        id="federated-search-input"
				:guide-contents="guideContents"
      />
      <div class="federated-results__results-list">
        <div class="federated-results__header-bar">
          <p role="status" aria-live="polite" aria-atomic="true">{{ totalResults }} results</p>
          <div class="federated-results__header-bar-clear-button">
            <button
              type="button"
              v-if="this.selectedFilters.length > 0"
              @click="this.clearAllFilters"
              aria-label="Clear All Filters"
            >
              <svg aria-hidden="true" role="img">
                <use
                  :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#clear-all`"
                /></svg
              >Clear All
            </button>
          </div>
          <div class="federated-results__header-bar-icons">
            <button
              type="button"
              @click="viewMode = 'grid'"
              :aria-current="viewMode === 'grid'"
              aria-label="Display Grid View"
            >
              <svg
                aria-hidden="true"
                role="img"
                :class="viewMode === 'grid' ? 'active' : ''"
              >
                <use
                  :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#grid-icon`"
                />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Display List View"
              :aria-current="viewMode === 'list'"
              @click="viewMode = 'list'"
            >
              <svg
                :class="viewMode === 'list' ? 'active' : ''"
                aria-hidden="true"
                role="img"
              >
                <use
                  :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#list-icon`"
                />
              </svg>
            </button>
          </div>
        </div>
        <div class="federated-results__filter-and-clear-button">
          <button
            class="federated-results__header-bar-filter-button"
            type="button"
            @click="showFilter"
            @keydown.tab="handleTabOffFilterButton"
          >
            <svg aria-hidden="true" role="img">
              <use
                :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#filter-icon`"
              />
            </svg>
            <div>Filter</div>
          </button>

          <div
            class="federated-results__header-bar-clear-button federated-results__header-bar-clear-button--mobile"
          >
            <button
              type="button"
              v-if="this.selectedFilters.length > 0"
              @click="this.clearAllFilters"
              aria-label="Clear All Filters"
            >
              <svg aria-hidden="true" role="img">
                <use
                  :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#clear-all`"
                /></svg
              >Clear All
            </button>
          </div>
        </div>
        <div class="federated-results__filters-and-results">
          <div
            :class="`federated-results__filters ${
              displayFilters === true
                ? 'federated-results__filters--active'
                : ''
            }`"
          >
            <div
              class="federated-results__filters-container"
              :tab-index="displayFilters ? 0 : -1"
            >
              <button
                type="button"
                v-if="displayFilters"
                @click="hideFilter"
                ref="closeFilterButton"
                class="federated-results__filters-close-button"
              >
                <span class="sr-only">Close Filters</span>
                <svg aria-hidden="true" role="img">
                  <use
                    :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#close-icon`"
                  />
                </svg>
              </button>
              <div
                v-for="(facet, index) in this.displayableFacetFilters"
                v-bind:key="index"
              >
                <ahc-federated-search-facet-filter
                  :facet="facet"
                  :selected-filters="selectedFilters"
                  :search="search"
                  @update:selectedFilters="selectedFilters = $event"
                />
              </div>
              <ahc-federated-search-facet-date-filter
                :search="this.search"
                :year-range-start="yearRangeStart"
                :year-range-end="yearRangeEnd"
                @update:yearRangeStart="yearRangeStart = $event"
                @update:yearRangeEnd="yearRangeEnd = $event"
                :handle-tab-off-submit="handleTabOffSubmit"
              />
            </div>
          </div>
          <div
            v-if="viewMode === 'list' || !isDesktop()"
            class="federated-results__results"
          >
            <p v-if="Object.keys(facetFilters).length && (!results || !results.length)" class="federated-results__results-no-results">{{noResultsText}}</p>
            <ahc-federated-search-list-item
              ref="listItem"
              :result="result"
              :display-detail="detail === result.objectID"
              :details-config="detailsConfig"
              v-for="result in results"
              v-bind:key="result.objectID"
            />
          </div>
          <div
            v-if="viewMode === 'grid' && isDesktop()"
            class="federated-results__results federated-results__results--grid"
          >
            <div
              class="federated-results__results-column"
              v-for="(results, index) in this.resultGroups()"
              v-bind:key="index"
            >
              <ahc-federated-search-list-item
                :result="result"
                :grid="true"
                :display-detail="detail === result.objectID"
                :details-config="detailsConfig"
                v-for="result in results"
                v-bind:key="result.objectID"
              />
            </div>
          </div>
        </div>
        <ahc-paginator
          class="federated-results__paginator"
          v-if="totalResults > hitsPerPage"
          :update-page="updatePage"
          :total-pages="totalPages"
          :current-page="currentPage"
        />
      </div>
    </div>
  </div>
</template>

<script lang="js">
import algoliasearch from 'algoliasearch/lite';
import ahcPaginator from './../paginator/paginator.vue';
import ahcFederatedSearchSearchbar from './../federated-search-searchbar/federated-search-searchbar.vue';
import ahcFederatedSearchListItem from './../federated-search-list-item/federated-search-list-item.vue';
import ahcFederatedSearchFacetDateFilter from "./../federated-search-facet-date-filter/federated-search-facet-date-filter";
import ahcFederatedSearchFacetFilter from "./../federated-search-facet-filter/federated-search-facet-filter";


  export default  {
    name: 'ahc-federated-search-results',
    components: {
      ahcPaginator,
      ahcFederatedSearchSearchbar,
      ahcFederatedSearchListItem,
      ahcFederatedSearchFacetDateFilter,
      ahcFederatedSearchFacetFilter,
    },
    // Handles grabbing any urls from the url and assigning them to local variables for deeplinking
    beforeMount() {
      const uri = window.location.search.substring(1);
      const params = new URLSearchParams(uri);

      const getDecodedParam = (param) => {
        return params.has(param) ? decodeURI(params.get(param)) : null;
      };

      this.searchParam = getDecodedParam("s") ?? '';
      this.detail = getDecodedParam("detail");
      this.yearRangeEnd = getDecodedParam("yearRangeEnd");
      this.yearRangeStart = getDecodedParam("yearRangeStart");
      this.currentPage = params.has("page") ? parseInt(params.get("page")) - 1 : 0;

      // We have to call search twice on load to get the initial available facets
      this.search({initialLoad: true});

      const url = new URL(window.location.href);
      this.selectedFilters = url?.searchParams?.get("filters")?.split(",").map((f) => decodeURI(f)) ?? [];

      this.search({});
    },
    mounted() {
      window.addEventListener('resize', () => {this.windowResize(this)})

      // Escape key listener for the filters modal
      document.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.displayFilters) this.displayFilters = false;
      })
    },
    unmounted() {
      window.removeEventListener('resize', this.windowResize);
    },
    methods: {
      // Clears all filter on the clear button click
      clearAllFilters() {
        this.selectedFilters = [];
        this.yearRangeEnd = null;
        this.yearRangeStart = null;
        this.search({page: 0});
      },
      // Pagination callback to update the page number and perform a new search
      updatePage(page) {
        this.search({page});
        this.$refs.results.scrollIntoView();
      },
      // Submit handler for when the search query changes in the search bar
      submitHandler(searchValue) {
        this.searchParam = searchValue;
        this.clearAllFilters();
        this.search({page: 0});
      },
      // Click handler to display the filter drawer on mobile
      showFilter() {
        document.body.classList.add("modal-open");
        this.displayFilters = true;
        this.$nextTick(() => {
          this.$refs.closeFilterButton.focus();
        });
      },
      // This addresses accessilibity tabbing within the filter modal
      handleTabOffSubmit(e) {
        if (this.displayFilters) {
          e.preventDefault();
          this.$refs.closeFilterButton.focus();
        }
      },
      // This address accessibility tabbing when at mobile and the filters are closed
      handleTabOffFilterButton(e) {
        if (!this.displayFilters) {
          e.preventDefault();
          this.$refs.listItem[0].$el.querySelector('a').focus();
        }
      },
      // Click handler that closes the filter drawer on mobile
      hideFilter() {
        document.body.classList.remove("modal-open");
        this.displayFilters = false;
        this.$refs.results.scrollIntoView();
      },
      // Returns if we are at the Desktop view for responsive code
      isDesktop () {
        return this.innerWidth > 768;
      },
      // Formats a the selected facet filter for Algolia
      formattedFacetFilters() {
        const keyedFacets = {};
        this.selectedFilters.forEach((filter) => {
          const split = filter.split(":");
          const key = split[0];
          if (!keyedFacets[key]) {
            keyedFacets[key] = [];
          }
          keyedFacets[key].push(filter);
        });
        return Object.values(keyedFacets);
      },
      // This retrieves the list of displayable facets and formats them for
      // use with the ahc-federated-search-facet-filter component.
      formatDisplayableFacetFilters () {
        this.displayableFacetFilters =  this.config.filters.split(',')
          .map((filter) => {
            const existingFilter = this.displayableFacetFilters?.find((f) => f.name === filter);
            let displayCount = existingFilter ? existingFilter.displayCount : 10;

            // Find the last increment of 10 that includes a select filter. This is for deeplinking
            // and displaying the correct amount fo show all preselected options
            Object.keys(this.facetFilters[filter] ?? []).forEach((facetKey, index) => {
              if (this.selectedFilters.includes(`${filter}:${facetKey}`))
                {
                  displayCount = index + 1;
                }
            })

            // This adds a configurable boolean based on a config from the cms to auto collapse certain facet filters
            const collapsedByDefault = this.config.search_filters_collapse_by_default.split(',').includes(filter);

            return {options: this.facetFilters[filter] ?? {}, name: filter, collapsedByDefault, displayCount: Math.ceil(displayCount / 10) * 10}
          // Removes any facet filters that don't have any available props from the array
          }).filter((facetFilter) => !(Object.keys(facetFilter).length === 0 && !facetFilter.__ob__.value));
      },
      // This updates the url params whenever a local parameter changes
      updateParams() {
        const url = new URL(window.location.href);
        url.searchParams.set('s', this.searchParam);
        url.searchParams.set('page', this.currentPage + 1);

        const filters = this.selectedFilters;
        if (filters.length) {
          url.searchParams.set('filters', encodeURI(filters.join(',')));
        } else {
          url.searchParams.delete('filters');
        }

        if (this.results?.length && !this.results.some((result) => result.objectID === this.detail)) {
          this.detail = null;
          url.searchParams.delete('detail')
        }

        this.updateQueryParam(url, 'yearRangeEnd', this.yearRangeEnd);
        this.updateQueryParam(url, 'yearRangeStart', this.yearRangeStart);

        window.history.replaceState(null, '', url);
      },
      // Helper to update a param in the url
      updateQueryParam(url, param, value) {
        if (value) {
          url.searchParams.set(param, encodeURI(value));
        } else {
          url.searchParams.delete(param);
        }
      },
      windowResize: (context) => { context.innerWidth = window.innerWidth },
      // Algolia search method
      async search({ option, page, initialLoad = false }) {
        const { optionalWords, queryString } = this.extractOrPhrases();

        const { hits, nbHits, facets, page: currentPage, nbPages } = await this.index.search(queryString, {
          page: page ?? this.currentPage,
          filters: this.yearRangeFilters(),
          hitsPerPage: this.hitsPerPage,
          facets: ["*"],
          facetFilters: this.formattedFacetFilters(),
          optionalWords
        });

        const shouldUpdate = !this.selectedFilters.some(filter => filter.includes(`${option}:`));
        const currentFacet = option && !shouldUpdate ? { [option]: this.facetFilters[option] } : {};
        this.facetFilters = { ...facets, ...currentFacet };
        this.totalResults = nbHits;
        this.totalPages = nbPages;
        this.currentPage = currentPage;

        if (!initialLoad) this.results = hits;

        this.formatDisplayableFacetFilters();
        this.updateParams();
      },
      // Formats the year range for Algolia
      yearRangeFilters () {
        if (this.yearRangeEnd && this.yearRangeStart) {
          const endSeconds = new Date(parseInt(this.yearRangeEnd) + 1, 0, 1) / 1000;
          const startSeconds = new Date(parseInt(this.yearRangeStart), 0, 1) / 1000;
          return `"date begin":${startSeconds} TO ${endSeconds} OR "date end":${startSeconds} TO ${endSeconds}`;
        } else {
          return '';
        }
      },
      // Formats the query string to handle "OR" logic in Algolia
      extractOrPhrases() {
        let queryString = this.searchParam;
        let optionalWords = [];

        if (queryString) {
          const pattern = /"([^"]*)"/g;
          const quotedPhrases = this.searchParam.match(pattern) || [];

          quotedPhrases.forEach((phrase) => {

            const isOr = this.searchParam.includes(` or ${phrase}`) ||
              this.searchParam.includes(`${phrase} or "`) ||
              this.searchParam.includes(`" OR ${phrase}`) ||
              this.searchParam.includes(`${phrase} OR "`);

            if (isOr) {
              optionalWords.push(phrase.replace(/["']/g, ""));
              queryString = queryString.replace(`or ${phrase}`, phrase, "");
              queryString = queryString.replace(`${phrase} or`, phrase, "");
              queryString = queryString.replace(`OR ${phrase}`, phrase, "");
              queryString = queryString.replace(`${phrase} OR`, phrase, "");
              queryString = queryString.replace(`${phrase}`, phrase, "");
            }
          })
        }

        return {
          optionalWords,
          queryString,
        };
      },
      // This returns the correct column in groups for the tile view depending on the breakpoint
      resultGroups () {
        const columns = this.isDesktop() ? 3 : 2;
        const itemsPerColumn = Math.ceil(this.results.length/columns);
        const groups =  Array.from({ length: Math.ceil(this.results.length / itemsPerColumn) }, (v, i) =>
          this.results.slice(i * itemsPerColumn, i * itemsPerColumn + itemsPerColumn)
        );
        return groups;
      },
    },
    props: {
      headerCopy: {
        type: String,
        required: true,
        default: ""
      },
      helpText: {
        type: String,
        required: true,
      },
      detailsConfig: {
        type: Object,
        required: true,
      },
      config: {
        type: Object,
        required: true
      },
      breadcrumb: {
        type: Object,
        required: true
      },
      guideText: {
        type: String,
        required: false
      },
      guideLink: {
        type: String,
        required: false
      },
      guideContents: {
        type: String,
        required: false
      },
      noResultsText: {
        type: String,
        required: false
      },
    },
    data () {
      return {
        innerWidth: window.innerWidth,
        index: algoliasearch(
          this.config.application_id,
          this.config.application_key
        ).initIndex(this.config.index_name),
        viewMode: 'list',
        collapsed: [],
        totalResults: 0,
        totalPages: 0,
        currentPage: 0,
        hitsPerPage: 20,
        detail: null,
        searchParam: '',
        selectedFilters: [],
        displayFilters: false,
        results: null,
        facetFilters: [],
        yearRangeStart: null,
        yearRangeEnd: null,
        displayableFacetFilters: []
      }
    }
}
</script>

<style lang="scss">
@import "federated-search-results";
</style>
